import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LuChevronDown, LuMenu, LuX, LuArrowRight } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import YrosLogo from '../../assets/logo1.png';
import COUNTRIES from '../../configs/countries';
import LanguageSwitcher from './LanguageSwitcher';

const Header = ({ country, setCountry }) => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [countryOpen, setCountryOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  const menuCategories = [
    {
      nameKey: "header.features_category",
      subItems: [
        { key: "header.features.employees", link: "/" },
        { key: "header.features.payslips", link: "/" },
        { key: "header.features.accounting_export", link: "/" },
        { key: "header.features.leave_tracking", link: "/" },
        { key: "header.features.interns", link: "/" },
        { key: "header.features.tax_declarations", link: "/" },
        { key: "header.features.overtime_tracking", link: "/" }
      ]
    }
  ];

  return (
    <header className={`sticky top-0 z-50 w-full bg ${isScrolled ? 'shadow-md bg-white' : 'bg-orange-50'} transition-all duration-300`}>
      <div className={`max-w-7xl mx-auto px-4 md:px-6 lg:px-8 transition-all duration-300 
        ${isScrolled ? 'py-3' : 'py-4'}`}>
        
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <motion.img 
              src={YrosLogo} 
              alt="Yros Logo" 
              className={`h-10 transition-all duration-300 ${isScrolled ? 'h-9' : 'h-10'}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            />
          </Link>

          {/* Navigation - Desktop */}
          <nav className="hidden lg:flex items-center space-x-1 ml-6">
            {/* {menuCategories.map((category, idx) => (
              <div key={idx} className="relative group">
                <button
                  onClick={() => toggleDropdown(idx)}
                  className="flex items-center px-3 py-2 rounded-lg text-gray-800 hover:text-orange-600 hover:bg-orange-50 transition-colors group-hover:text-orange-600"
                >
                  <span className="font-medium">{category.name}</span>
                  <LuChevronDown className={`ml-1 w-4 h-4 transition-transform duration-200 
                    ${activeDropdown === idx ? 'transform rotate-180' : ''}`} 
                  />
                </button>

                <AnimatePresence>
                  {(activeDropdown === idx || true) && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, height: 0 }}
                      animate={{ opacity: 1, y: 0, height: 'auto', transition: { duration: 0.2 } }}
                      exit={{ opacity: 0, y: 10, height: 0, transition: { duration: 0.2 } }}
                      className="absolute left-0 mt-1 py-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 hidden group-hover:block"
                      onMouseLeave={() => setActiveDropdown(null)}
                    >
                      <div className="grid grid-cols-1 gap-1 p-2">
                        {category.subItems.map((item, itemIdx) => (
                          <Link
                            key={itemIdx}
                            to={item.link}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-lg hover:bg-orange-50 text-gray-700 hover:text-orange-600 transition-colors"
                          >
                            <span className="font-medium">{item.name}</span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))} */}

            <div className="relative">
              {/* Prominent country selector button */}
              <button
                onClick={() => setCountryOpen(!countryOpen)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-semibold shadow-lg transition transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-orange-200 ${isScrolled ? 'bg-white text-gray-800 border border-orange-200' : 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white'}`}
                aria-haspopup="menu"
                aria-expanded={countryOpen}
                type="button"
                title={t('header.country_selector_title')}
              >
                {/* <span className="text-2xl leading-none">{COUNTRIES.find(c => c.code === country)?.flag}</span> */}
                <span className="ml-1">{COUNTRIES.find(c => c.code === country)?.name}</span>
                <LuChevronDown className={`ml-2 w-4 h-4 transition-transform ${countryOpen ? 'transform rotate-180' : ''}`} />
              </button>

              {countryOpen && (
                <div
                  className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-2xl border border-orange-100 z-50"
                  onMouseLeave={() => setCountryOpen(false)}
                >
                  <div className="p-3 space-y-1">
                    {COUNTRIES.map(c => (
                      <button
                        key={c.code}
                        onClick={() => { if (c.available) { setCountry(c.code); } setCountryOpen(false); }}
                        className={`w-full text-left flex items-center justify-between gap-3 px-3 py-3 rounded-xl transition-colors ${c.code === country ? 'bg-orange-50 border border-orange-200' : 'hover:bg-orange-50'} ${!c.available ? 'opacity-60 cursor-not-allowed' : ''}`}
                        disabled={!c.available}
                        type="button"
                      >
                        <div className="flex items-center gap-3">
                          {/* <span className="text-2xl">{c.flag}</span> */}
                          <div>
                            <div className="font-medium text-gray-800">{c.name}</div>
                            <div className="text-xs text-gray-500">{c.available ? t('header.country_available') : t('header.country_in_development')}</div>
                          </div>
                        </div>
                        {!c.available && <span className="text-xs text-yellow-700">{t('header.country_in_dev_short')}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/about" className="px-3 py-2 rounded-lg text-gray-800 hover:text-orange-600 hover:bg-orange-50 transition-colors font-medium">
              {t('header.nav_about')}
            </Link>

            <Link to="/contact" className="px-3 py-2 rounded-lg text-gray-800 hover:text-orange-600 hover:bg-orange-50 transition-colors font-medium">
              {t('header.nav_contact')}
            </Link>

            <LanguageSwitcher />
          </nav>

          {/* Country selector + actions */}
          <div className="hidden lg:flex items-center gap-4 ml-auto">
            <Link
              to="/simulateur"
              className="px-4 py-2 rounded-xl text-gray-800 border border-orange-300 hover:bg-orange-100 hover:border-orange-400 transition-colors font-medium text-sm"
            >
              {t('header.cta_simulator')}
            </Link>

            <Link
              to="/demo"
              className="px-5 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-medium text-sm shadow-sm hover:shadow transition-all flex items-center gap-2 group"
            >
              {t('header.cta_demo')}
              <LuArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors"
              aria-label={mobileMenuOpen ? t('header.close_menu_aria') : t('header.open_menu_aria')}
            >
              {mobileMenuOpen ? <LuX className="h-6 w-6" /> : <LuMenu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-gray-100 shadow-lg"
          >
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-3">
              {/* Mobile language switcher */}
              <div className="flex justify-start">
                <LanguageSwitcher />
              </div>

              {/* Mobile country selector */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">{t('header.mobile_country_label')}</div>
                <div className="flex gap-2 flex-wrap">
                  {COUNTRIES.map(c => (
                    <button
                      key={c.code}
                      onClick={() => { if (c.available) { setCountry(c.code); } setMobileMenuOpen(false); }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border shadow-sm text-sm ${country === c.code ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white border-orange-400' : 'bg-white border-gray-200 text-gray-800'} transition-transform hover:scale-[1.02] ${!c.available ? 'opacity-70 cursor-not-allowed' : ''}`}
                      disabled={!c.available}
                      type="button"
                    >
                      <span className="text-2xl">{c.flag}</span>
                      <div className="text-left">
                        <div className={`${country === c.code ? 'font-semibold' : 'font-medium'}`}>{c.name}</div>
                        {c.available ? <div className="text-xs text-white/80 hidden">&nbsp;</div> : <div className="text-xs text-yellow-700">{t('header.country_in_dev_short')}</div>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              {menuCategories.map((category, idx) => (
                <div key={idx} className="space-y-2">
                  <button
                    onClick={() => toggleDropdown(idx)}
                    className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-gray-800 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                  >
                    <span className="font-medium">{t(category.nameKey)}</span>
                    <LuChevronDown className={`ml-1 w-4 h-4 transition-transform duration-200 ${activeDropdown === idx ? 'transform rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {activeDropdown === idx && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="pl-4 space-y-1"
                      >
                        {category.subItems.map((item, itemIdx) => (
                          <Link
                            key={itemIdx}
                            to={item.link}
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex px-3 py-2 rounded-lg text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                          >
                            {t(item.key)}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-gray-800 hover:bg-orange-50 hover:text-orange-600 transition-colors"
              >
                {t('header.nav_about')}
              </Link>

              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-gray-800 hover:bg-orange-50 hover:text-orange-600 transition-colors"
              >
                {t('header.nav_contact')}
              </Link>

              <div className="pt-2 space-y-3">
                <Link
                  to="/simulateur"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full px-4 py-2.5 rounded-xl text-gray-800 border border-orange-300 hover:bg-orange-100 text-center font-medium"
                >
                  {t('header.cta_simulator')}
                </Link>

                <Link
                  to="/demo"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full px-4 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-yellow-500 text-white text-center font-medium"
                >
                  {t('header.cta_demo')}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;